import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Alert, Button, DatePicker, Select, Skeleton, Space, Typography, theme } from 'antd';
import { CalendarOutlined, EnvironmentOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import {
  RUTA_COBROS_ESTADO,
  RUTA_COBROS_TIPO,
  UBICACION_ORIGEN,
  type RutaCobroEntry,
  type RutaCobrosEstado,
} from '@aid/shared';
import { DashboardCard } from '../../../components/DashboardCard';
import { EmptyState } from '../../../components/EmptyState';
import { StatusTag, type StatusTagTone } from '../../../components/StatusTag';
import { coordenadasValidas } from '../../../lib/geo';
import { DATE_FORMAT, parseDateOnly } from '../../../lib/date';
import { formatCurrency } from '../../../lib/format';
import { i18nInstance, useT } from '../../../locales';
import { useRutaCobros } from '../api/useDashboard';
import { formatRutaCobroRegistrationTime, todayInTimeZone } from '../lib/ruta-cobros';
import { VisitaResultadoTag } from '../../visitas/components/VisitaResultadoTag';
import { RutaCobrosMap } from './RutaCobrosMap';
import './ruta-cobros.css';

const { Text } = Typography;

type RutaCobrosTranslate = (key: string, options?: Record<string, unknown>) => string;

interface RutaCobroRowProps {
  entry: RutaCobroEntry;
  number: number;
  selected: boolean;
  locale: string;
  t: RutaCobrosTranslate;
  token: ReturnType<typeof theme.useToken>['token'];
  onSelect: () => void;
  rowRef: (node: HTMLButtonElement | null) => void;
}

function statusTone(status: RutaCobrosEstado): StatusTagTone {
  return status === RUTA_COBROS_ESTADO.ANULADO ? 'error' : 'success';
}

function statusLabel(status: RutaCobrosEstado, t: RutaCobrosTranslate): string {
  return status === RUTA_COBROS_ESTADO.ANULADO
    ? t('dashboard.rutaCobros.statusAnnulled')
    : t('dashboard.rutaCobros.statusCurrent');
}

function paymentInstant(value: Date | string): Date | null {
  const instant = value instanceof Date ? value : new Date(value);
  return Number.isFinite(instant.getTime()) ? instant : null;
}

function rutaCobroEntryKey(entry: RutaCobroEntry): string {
  return `${entry.tipo}:${entry.id}`;
}

function RutaCobroRow({
  entry,
  number,
  selected,
  locale,
  t,
  token,
  onSelect,
  rowRef,
}: RutaCobroRowProps) {
  const isVisit = entry.tipo === RUTA_COBROS_TIPO.VISITA;
  const registrationInstant = paymentInstant(entry.fechaCreacion);
  const registrationTime =
    formatRutaCobroRegistrationTime(entry.fechaCreacion, locale) ?? t('common.notAvailable');
  const scheduledVisitDate = isVisit
    ? (parseDateOnly(entry.fecha)?.format(DATE_FORMAT) ?? t('common.notAvailable'))
    : null;
  const clientName =
    entry.cliente?.nombre?.trim() ||
    t('dashboard.rutaCobros.clientFallback', { id: entry.idCliente });
  const collectorName = entry.cobrador.nombre.trim() || t('common.notAvailable');
  const coordinate = coordenadasValidas(entry.latitud, entry.longitud);
  const location = coordinate
    ? t('dashboard.rutaCobros.locationCoordinates', {
        lat: coordinate.lat.toFixed(5),
        lng: coordinate.lng.toFixed(5),
      })
    : t('dashboard.rutaCobros.noLocation');
  const locationSource = coordinate
    ? entry.ubicacionOrigen === UBICACION_ORIGEN.CLIENTE
      ? t('dashboard.rutaCobros.locationFromClient')
      : entry.ubicacionOrigen === UBICACION_ORIGEN.GPS
        ? t('dashboard.rutaCobros.locationFromGps')
        : null
    : null;
  const style: CSSProperties = {
    border: `1px solid ${
      selected ? token.colorPrimary : isVisit ? token.colorWarning : token.colorBorderSecondary
    }`,
    background: selected ? token.colorPrimaryBg : token.colorBgContainer,
  };

  return (
    <button
      ref={rowRef}
      type="button"
      data-testid={`ruta-cobros-row-${entry.id}`}
      className={`ruta-cobros-entry${isVisit ? ' ruta-cobros-entry--visit' : ''}${selected ? ' ruta-cobros-entry--selected' : ''}`}
      style={style}
      aria-pressed={selected}
      aria-label={
        isVisit
          ? t('dashboard.rutaCobros.visitRowLabel', {
              number,
              registration: registrationTime,
              scheduledDate: scheduledVisitDate,
              result: t(`pages.visitas.resultados.${entry.resultado}`),
              collector: collectorName,
              client: clientName,
            })
          : t('dashboard.rutaCobros.paymentRowLabel', {
              number,
              registration: registrationTime,
              collector: collectorName,
              client: clientName,
              amount: formatCurrency(entry.valor),
            })
      }
      onClick={onSelect}
    >
      <span className="ruta-cobros-entry__topline">
        <span className="ruta-cobros-entry__number" aria-hidden="true">
          {number}
        </span>
        <span className="ruta-cobros-entry__registration">
          {t('dashboard.rutaCobros.registrationTime')}:{' '}
          <time dateTime={registrationInstant?.toISOString()}>{registrationTime}</time>
        </span>
        {isVisit && (
          <span className="ruta-cobros-entry__type">{t('dashboard.rutaCobros.visitType')}</span>
        )}
        {isVisit ? (
          <VisitaResultadoTag resultado={entry.resultado} />
        ) : (
          <StatusTag tone={statusTone(entry.estadoPago)}>
            {statusLabel(entry.estadoPago, t)}
          </StatusTag>
        )}
      </span>
      <span className="ruta-cobros-entry__mainline">
        <Text strong>{clientName}</Text>
        {isVisit ? (
          <span className="ruta-cobros-entry__scheduled">
            <CalendarOutlined aria-hidden="true" />
            <Text strong>
              {t('dashboard.rutaCobros.scheduledVisit')}:{' '}
              <time dateTime={entry.fecha}>{scheduledVisitDate}</time>
            </Text>
          </span>
        ) : (
          <Text strong>{formatCurrency(entry.valor)}</Text>
        )}
      </span>
      <span className="ruta-cobros-entry__detail">
        <Text type="secondary">
          {t('dashboard.rutaCobros.collector')}: {collectorName}
        </Text>
      </span>
      {isVisit && entry.comentario?.trim() && (
        <span className="ruta-cobros-entry__detail">
          <Text type="secondary">
            {t('dashboard.rutaCobros.visitComment')}: {entry.comentario.trim()}
          </Text>
        </span>
      )}
      <span className="ruta-cobros-entry__detail ruta-cobros-entry__location">
        <EnvironmentOutlined aria-hidden="true" />
        <span>
          {t('dashboard.rutaCobros.location')}: {location}
          {locationSource && (
            <span className="ruta-cobros-entry__location-source"> ({locationSource})</span>
          )}
        </span>
      </span>
    </button>
  );
}

export function RutaCobros() {
  const t = useT();
  const { token } = theme.useToken();
  const [selectedDate, setSelectedDate] = useState(() => todayInTimeZone());
  const [selectedCollector, setSelectedCollector] = useState<string | undefined>();
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [selectedEntryKey, setSelectedEntryKey] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const params = selectedCollector
    ? { fecha: selectedDate, idUsuario: selectedCollector }
    : { fecha: selectedDate };
  const query = useRutaCobros(params);
  const response = query.data;
  const entries = response?.data ?? [];
  const collectors = response?.cobradores ?? [];
  const locale = i18nInstance.language;
  const initialLoading = query.isLoading && !response;

  useEffect(() => {
    if (selectedEntryKey === null) return;
    const row = rowRefs.current.get(selectedEntryKey);
    if (!row) return;
    row.focus({ preventScroll: true });
    row.scrollIntoView?.({ block: 'nearest' });
  }, [selectedEntryKey]);

  const selectEntry = (entry: RutaCobroEntry) => {
    setSelectedEntryKey(rutaCobroEntryKey(entry));
    setSelectedPaymentId(entry.tipo === RUTA_COBROS_TIPO.PAGO ? entry.id : null);
  };

  const selectPayment = (id: number) => {
    setSelectedEntryKey(`${RUTA_COBROS_TIPO.PAGO}:${id}`);
    setSelectedPaymentId(id);
  };

  const handleDateChange = (value: Dayjs | null) => {
    const nextDate = value?.format('YYYY-MM-DD');
    if (!nextDate) return;
    setSelectedDate(nextDate);
    setSelectedCollector(undefined);
    setSelectedPaymentId(null);
    setSelectedEntryKey(null);
  };

  const controls = (
    <div className="ruta-cobros-controls" aria-label={t('dashboard.rutaCobros.controlsLabel')}>
      <Space wrap size={[12, 12]}>
        <label htmlFor="ruta-cobros-date">{t('dashboard.rutaCobros.dateLabel')}</label>
        <DatePicker
          id="ruta-cobros-date"
          value={dayjs(selectedDate)}
          format="DD/MM/YYYY"
          allowClear={false}
          onChange={handleDateChange}
        />
        <label htmlFor="ruta-cobros-collector">{t('dashboard.rutaCobros.collectorLabel')}</label>
        <Select<string>
          id="ruta-cobros-collector"
          aria-label={t('dashboard.rutaCobros.collectorLabel')}
          allowClear
          value={selectedCollector}
          placeholder={t('dashboard.rutaCobros.collectorPlaceholder')}
          options={collectors.map((collector) => ({
            value: collector.id,
            label: collector.nombre,
          }))}
          disabled={initialLoading || collectors.length === 0}
          loading={initialLoading}
          onChange={(value) => {
            setSelectedCollector(value);
            setSelectedPaymentId(null);
            setSelectedEntryKey(null);
          }}
          style={{ minWidth: 230 }}
        />
        <Button
          icon={<ReloadOutlined />}
          loading={query.isRefetching}
          onClick={() => void query.refetch()}
        >
          {t('dashboard.rutaCobros.refresh')}
        </Button>
      </Space>
    </div>
  );

  const body = initialLoading ? (
    <div className="ruta-cobros-loading-grid">
      <DashboardCard title={t('dashboard.rutaCobros.listTitle')}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </DashboardCard>
      <DashboardCard title={t('dashboard.rutaCobros.mapTitle')}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </DashboardCard>
    </div>
  ) : query.isError && !response ? (
    <DashboardCard>
      <EmptyState
        title={t('dashboard.rutaCobros.errorTitle')}
        description={t('dashboard.rutaCobros.errorDescription')}
        action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
      />
    </DashboardCard>
  ) : entries.length === 0 ? (
    <DashboardCard>
      <EmptyState
        title={t('dashboard.rutaCobros.emptyTitle')}
        description={t('dashboard.rutaCobros.emptyDescription')}
      />
    </DashboardCard>
  ) : (
    <>
      {query.isError && response && (
        <Alert
          type="warning"
          showIcon
          message={t('dashboard.rutaCobros.refreshError')}
          style={{ marginBottom: 16 }}
        />
      )}
      <div className="ruta-cobros-grid">
        <DashboardCard
          title={t('dashboard.rutaCobros.listTitle')}
          subtitle={t('dashboard.rutaCobros.listSubtitle', { count: entries.length })}
          style={{ height: '100%' }}
          bodyStyle={{ padding: 12, height: '100%' }}
        >
          <div
            className="ruta-cobros-list"
            role="list"
            aria-label={t('dashboard.rutaCobros.listTitle')}
          >
            {entries.map((entry, index) => (
              <RutaCobroRow
                key={rutaCobroEntryKey(entry)}
                entry={entry}
                number={index + 1}
                selected={selectedEntryKey === rutaCobroEntryKey(entry)}
                locale={locale}
                t={t}
                token={token}
                onSelect={() => selectEntry(entry)}
                rowRef={(node) => {
                  const key = rutaCobroEntryKey(entry);
                  if (node) rowRefs.current.set(key, node);
                  else rowRefs.current.delete(key);
                }}
              />
            ))}
          </div>
        </DashboardCard>
        <DashboardCard
          title={t('dashboard.rutaCobros.mapTitle')}
          subtitle={t('dashboard.rutaCobros.mapSubtitle')}
          style={{ height: '100%' }}
          bodyStyle={{ padding: 0, height: '100%' }}
        >
          <RutaCobrosMap
            entries={entries}
            selectedPaymentId={selectedPaymentId}
            selectedEntryKey={selectedEntryKey}
            onSelectPayment={selectPayment}
            onSelectEntry={selectEntry}
          />
        </DashboardCard>
      </div>
    </>
  );

  return (
    <div className="ruta-cobros" data-testid="ruta-cobros-view">
      {controls}
      {body}
    </div>
  );
}
